import styled, { css } from 'styled-components';

import { roundSelectionIndicatorStyles } from './sharedStyles';
import { a11yInvisibleStyle } from '~/components/shared/common/styles';

export const Option = styled.section``;

export const OptionItemBtn = styled.button`
  ${({ theme }) => css`
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    color: ${theme.colors.content.primary};
    font-size: ${theme.font.size[4]};
    font-weight: ${theme.variables.font.weight.medium};
    padding: ${theme.padding.md} ${theme.padding.none};
  `}
`;

export const OptionItemWrapper = styled.span`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.size['2']};
  `}
`;

export const OptionItemTitle = styled.span`
  ${({ theme }) => css`
    color: ${theme.colors.content.primary};
    font-size: ${theme.font.size[4]};
    font-weight: ${theme.variables.font.weight.medium};
    line-height: 1;
  `}
`;

export const OptionItemAppliedFilters = styled.span`
  ${({ theme }) => css`
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${theme.colors.content.secondary};
    font-size: ${theme.font.size[2]};
    font-weight: ${theme.variables.font.weight.regular};
    line-height: 1.4;
  `}
`;

export const Details = styled.div<{ $display: boolean }>`
  ${({ theme, $display }) => css`
    display: grid;
    grid-template-rows: ${$display ? '1fr' : '0fr'};
    transition: all 0.3s ease-in-out;
    opacity: ${$display ? '1' : '0'};
    padding: ${$display
      ? `${theme.padding['2xs']} ${theme.padding.none} ${theme.padding.lg} ${theme.padding.none}`
      : theme.padding.none};

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    > * {
      overflow: hidden;
    }
  `}
`;

export const DetailsList = styled.ul`
  ${({ theme }) => css`
    --border-width: ${({ theme }) => theme.borders.width.xs};
    --border-width-selected: ${({ theme }) => theme.borders.width.md};
    --border-color: ${({ theme }) => theme.colors.border.subtle};
    --border-color-selected: ${({ theme }) => theme.colors.border.base};

    display: flex;
    flex-direction: column;
    gap: ${theme.size['2']};
    margin: 0;
    padding: ${theme.padding.none};

    &.colorFilter {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    &.sizeFilter,
    &.lengthFilter {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding-bottom: 1px;

      .size-group {
        display: grid;
        grid-template-columns: repeat(auto-fit, 48px);
        gap: unset;

        li {
          margin: 0 -1px -1px 0;
          min-height: 48px;
          min-width: 48px;
          width: auto;
          box-shadow: inset 0 0 0 var(--border-width) var(--border-color);
          box-sizing: border-box;

          .selected {
            font-weight: ${theme.variables.font.weight.bold};
            color: ${theme.colors.content.primary};
          }

          &:has(.selected) {
            box-shadow: inset 0 0 0 var(--border-width) var(--border-color-selected),
              inset 0 calc(var(--border-width-selected) * -1) 0 0 var(--border-color-selected);
            z-index: 1;
          }
        }

        button {
          white-space: nowrap;
        }

        &-years {
          grid-template-columns: repeat(auto-fit, 56px);

          li {
            min-height: 56px;
            min-width: 57px;
          }
        }

        &-country,
        &-ranges {
          grid-template-columns: repeat(auto-fit, 56px);

          li {
            min-width: 57px;
          }
        }

        &-jp {
          grid-template-columns: repeat(auto-fit, 84px);

          li {
            min-width: 84px;
            min-height: 56px;
          }
        }

        &-special {
          grid-template-columns: repeat(1, 1fr);

          li {
            max-width: fit-content;

            button {
              text-transform: uppercase;
            }
          }
        }
      }
    }
  `}
`;

export const SizeGroupListItem = styled.li`
  width: 100%;
  list-style: none;
`;

export const SizeGroupList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const DetailsListItem = styled.li`
  list-style: none;
  width: fit-content;
`;

export const DetailBtn = styled.button`
  ${({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    min-height: 24px;
    color: ${theme.colors.content.primary};
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
    font-size: ${theme.font.size[3]};
    font-weight: ${theme.variables.font.weight.regular};
    padding-left: 32px;
  `}
`;

export const GridFilterBtn = styled.button`
  ${({ theme }) => css`
    --border-width: ${({ theme }) => theme.borders.width.xs};
    --border-width-selected: ${({ theme }) => theme.borders.width.md};
    --border-color: ${({ theme }) => theme.colors.border.subtle};
    --border-color-selected: ${({ theme }) => theme.colors.border.base};

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: inherit;
    font-size: ${theme.font.size[3]};
    font-weight: ${theme.variables.font.weight.medium};
    color: ${({ theme }) => theme.colors.content.secondary};
    transition: all 0.1s ease;
    width: 100%;

    &:focus {
      outline-offset: -3px;
    }

    &[data-long-label='true'] {
      font-size: ${theme.font.size[3]};
      padding: ${theme.padding['2xs']};
    }

    &[data-wide-label='true'] {
      flex-direction: column;
      font-size: ${theme.font.size[3]};
      line-height: 1.2;
      padding: ${theme.padding['2xs']};
      text-align: center;

      span:nth-child(2) {
        color: ${theme.colors.content.tertiary};
        font-weight: ${theme.variables.font.weight.regular};
        font-size: ${theme.font.size[2]};
      }
    }
  `}
`;

export const GridFilterItem = styled.li`
  list-style: none;
  width: 100%;

  &[data-span='full'] {
    grid-column: 1 / -1;
  }

  &[data-span='wide'] {
    max-width: fit-content;
  }
`;

export const Checkbox = styled.span<{ $checked?: boolean; $bgColor?: string }>`
  ${({ theme, $checked, $bgColor }) => css`
    ${$bgColor && $bgColor !== '#666666'
      ? `background-color: ${$bgColor};`
      : 'background: linear-gradient(221deg, #5098C7 17.31%, #89B750 34.33%, #F8D557 51.36%, #F57130 68.39%, #EB483F 85.42%)'};
    border: ${theme.borders.width.xs} solid ${theme.colors.border.component['input-elements'].rest};
    border-radius: ${theme.borders.radius.xs};
    display: inline-flex;
    justify-content: center;
    position: absolute;
    width: 24px;
    height: 24px;
    left: 0;
    box-sizing: border-box;

    .checkIcon {
      display: ${$checked ? 'block' : 'none'};
      width: 14px;
    }
  `};
`;

export const RoundCheckbox = styled.span<{ $selected?: boolean }>`
  ${roundSelectionIndicatorStyles}
`;

export const ShowMoreLessBtn = styled.button`
  ${({ theme }) => css`
    min-height: 24px;
    border-bottom: ${theme.borders.width.sm} solid ${theme.colors.content.tertiary};
    color: ${theme.colors.content.tertiary};
    font-size: ${theme.font.size[3]};
    font-weight: ${theme.variables.font.weight.medium};
  `}
`;

export const AppliedColor = styled.span<{ $bgColor: string }>`
  ${({ theme, $bgColor }) => css`
    display: inline-block;
    position: static;
    vertical-align: middle;
    margin-right: 6px;
    box-sizing: border-box;
    overflow: hidden;
    line-height: 20px;
    ${$bgColor && $bgColor !== '#666666'
      ? `background-color: ${$bgColor};`
      : 'background: linear-gradient(221deg, #5098C7 17.31%, #89B750 34.33%, #F8D557 51.36%, #F57130 68.39%, #EB483F 85.42%)'};
    ${$bgColor === '#FFFFFF' &&
    `border: ${theme.borders.width.xs} solid ${theme.colors.content.secondary}`};
    border-radius: ${theme.borders.radius.xs};
    width: ${theme.size['3']};
    height: ${theme.size['3']};
  `};
`;

export const ScreenReaderOnly = styled.span`
  ${a11yInvisibleStyle}
`;
